import {
  CRYPTO_SPRITE_PRICES,
  CRYPTO_USD_PER_CREDIT,
  CRYPTO_VOICE_MARGIN,
  cryptoUsdToCredits,
} from '../shared/crypto-packs.js';
import { CDN_URL } from '../config.js';
import { getByPath, getCurrentLanguage, interpolate } from '../translations.js';
import { PROVIDER_CREDIT_MULTIPLIERS, parseCreditMultiplier } from './creditMultiplier.js';
import { fetchCache } from './fetchCache.js';
import { fetchMembershipPlans, formatPlanNumber, formatPlanPrice, getPlanByTier } from './pricingPlans.js';

const PACKS = [
  { id: 'pack_9' },
  { id: 'pack_19', featured: true },
  { id: 'pack_39' },
  { id: 'pack_99' },
];

const IMAGE_CREDITS = CRYPTO_SPRITE_PRICES.image_base_anime;
const VIDEO_CREDITS = CRYPTO_SPRITE_PRICES.video_lowres_base;
const CONTEXT_TOKENS_FALLBACK = 32000;
const DEFAULT_TEXT_MULTIPLIER = 1;
const DEFAULT_VOICE_PROVIDER = 'elevenlabs';

const plans = () => window.CRYPTO_PLANS || {};

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[char]));

const pricingRoot = (lang) =>
  window.translations?.[lang]?.pricing
  ?? window.translations?.en?.pricing
  ?? {};

const cryptoText = (lang, path, fallback) => {
  const key = `pricing.crypto.${path}`;
  return getByPath(pricingRoot(lang), key)
    ?? getByPath(window.translations?.en?.pricing, key)
    ?? fallback;
};

const wholePackCount = (credits, unitCost) => {
  const unit = Number(unitCost);
  const balance = Number(credits);
  if (!Number.isFinite(unit) || unit <= 0 || !Number.isFinite(balance) || balance <= 0) return 0;
  return Math.floor(balance / unit);
};

const messageCount = (credits, creditsPerMessage) => wholePackCount(credits, creditsPerMessage);

const creditsPerMessage = (medianChatCost, multiplier) => {
  const median = Number(medianChatCost);
  const factor = Number(multiplier);
  if (!Number.isFinite(median) || median <= 0 || !Number.isFinite(factor) || factor <= 0) return 0;
  return cryptoUsdToCredits(median * factor);
};

const voiceCreditsPerMessage = (medianXaiVoiceCredits, provider) => {
  const base = Number(medianXaiVoiceCredits);
  const xaiMultiplier = PROVIDER_CREDIT_MULTIPLIERS.xai;
  if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(xaiMultiplier) || xaiMultiplier <= 0) return 0;
  const usdPerChar = provider === 'xai' ? 0.000015 : 0.00008;
  return cryptoUsdToCredits((base / xaiMultiplier) * usdPerChar, CRYPTO_VOICE_MARGIN);
};

const packBonusPercent = (usd, credits) => {
  const peg = Number(usd) / CRYPTO_USD_PER_CREDIT;
  const balance = Number(credits);
  if (!(peg > 0) || !(balance > 0)) return 0;
  return Math.max(0, Math.round((balance / peg - 1) * 100));
};

const voiceCreditsForPack = (pegVoiceCredits, packCredits, packUsd) => {
  const usd = Number(packUsd);
  const credits = Number(packCredits);
  const peg = Number(pegVoiceCredits);
  if (!(usd > 0) || !(credits > 0) || !(peg > 0)) return peg;
  const basis = usd / credits;
  return Math.round(peg * (CRYPTO_USD_PER_CREDIT / basis) * 100) / 100;
};

export const cryptoPackTitle = (lang = getCurrentLanguage()) =>
  cryptoText(lang, 'title', 'Buy credits');

export const cryptoSpendEstimate = (membershipPlans) => {
  const list = Array.isArray(membershipPlans) ? membershipPlans : [];
  const creator = getPlanByTier('harem', list);
  const median = Number(list.find((plan) => Number(plan?.median_chat_cost) > 0)?.median_chat_cost);
  const voiceMedian = Number(list.find((plan) => Number(plan?.median_xai_voice_credits) > 0)?.median_xai_voice_credits);
  const tokens = Number(creator?.max_context_tokens);
  return {
    medianChatCost: Number.isFinite(median) && median > 0 ? median : 0,
    medianXaiVoiceCredits: Number.isFinite(voiceMedian) && voiceMedian > 0 ? voiceMedian : 0,
    contextTokens: Number.isFinite(tokens) && tokens > 0 ? tokens : CONTEXT_TOKENS_FALLBACK,
  };
};

export const loadCryptoSpendEstimate = async () => {
  try {
    return cryptoSpendEstimate(await fetchMembershipPlans());
  } catch (error) {
    console.error('Failed to load crypto spend estimate:', error);
    return cryptoSpendEstimate(null);
  }
};

const positiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const renderPackCard = ({ id, featured }, lang, estimate) => {
  const plan = plans()[id];
  const creditValue = Number(plan?.credits ?? 0);
  const packUsd = Number(plan?.usd ?? 0);
  const credits = formatPlanNumber(creditValue, lang);
  const price = escapeHtml(formatPlanPrice(plan?.usd ?? '', lang));
  const tokens = formatPlanNumber(estimate.contextTokens, lang);
  const perk = (key, fallback, vars = {}) => escapeHtml(interpolate(cryptoText(lang, key, fallback), vars));
  const spend = (key, fallback, count) => perk(key, fallback, { count: formatPlanNumber(count, lang) });
  const cta = perk('cta', 'Get {count} credits', { count: credits });
  const heading = perk('spend_heading', '{count} credits, it translates to up to:', { count: credits });
  const bonusPercent = packBonusPercent(packUsd, creditValue);
  const bonusText = bonusPercent > 0
    ? perk('bonus', '+{percent}% value', { percent: bonusPercent })
    : '&nbsp;';
  const bonus = `<p class="crypto-pack-bonus"${bonusPercent > 0 ? '' : ' aria-hidden="true"'}>${bonusText}</p>`;
  const voiceUnit = voiceCreditsForPack(
    voiceCreditsPerMessage(estimate.medianXaiVoiceCredits, DEFAULT_VOICE_PROVIDER),
    creditValue,
    packUsd,
  );
  const voiceLine = voiceUnit > 0
    ? `<li data-crypto-spend="voice">${spend('voice', '~{count} voice messages', wholePackCount(creditValue, voiceUnit))}</li>`
    : '';
  return `
    <div class="pricing-plan${featured ? ' featured' : ''}" data-pricing-plan="${id}" data-pack-credits="${creditValue}" data-pack-usd="${packUsd}">
      <h2>${price}</h2>
      ${bonus}
      <p class="crypto-spend-heading">${heading}</p>
      <ul class="crypto-spend-list">
        <li data-crypto-spend="messages">${spend('messages', '~{count} messages', messageCount(creditValue, creditsPerMessage(estimate.medianChatCost, DEFAULT_TEXT_MULTIPLIER)))}</li>
        ${voiceLine}
        <li>${spend('images', '~{count} images', wholePackCount(creditValue, IMAGE_CREDITS))}</li>
        <li>${spend('videos', '~{count} videos', wholePackCount(creditValue, VIDEO_CREDITS))}</li>
      </ul>
      <ul class="crypto-perk-list">
        <li class="yes">${perk('perk_group', 'Group conversations')}</li>
        <li class="yes">${perk('perk_voice', 'Voice')}</li>
        <li class="yes">${perk('perk_models', 'Use any text model on OpenRouter')}</li>
        <li class="yes">${perk('perk_outfits', 'All outfits')}</li>
        <li class="yes">${perk('perk_context', '{tokens} context tokens', { tokens })}</li>
      </ul>
      <button class="plan-button crypto-plan-button" type="button" data-crypto-plan="${id}" aria-label="${cta}, ${price}">${cta}</button>
    </div>`;
};

const DEFAULT_ESTIMATE = Object.freeze({
  medianChatCost: 0,
  medianXaiVoiceCredits: 0,
  contextTokens: CONTEXT_TOKENS_FALLBACK,
});

const renderEstimateControls = (lang, estimate) => {
  const label = (key, fallback) => escapeHtml(cryptoText(lang, key, fallback));
  const note = label('estimate_note', 'Simulate how far a pack goes with the models you use.');
  return `
    <div class="crypto-estimate">
    <p class="crypto-estimate-note">${note}</p>
    <div class="crypto-estimate-controls" data-median-chat-cost="${estimate.medianChatCost}" data-median-xai-voice="${estimate.medianXaiVoiceCredits}">
      <label>
        <span>${label('text_model_label', 'Text model')}</span>
        <select data-crypto-text-model aria-label="${label('text_model_label', 'Text model')}">
          <option value="${DEFAULT_TEXT_MULTIPLIER}" selected>${label('text_model_default', 'Standard')}</option>
        </select>
      </label>
      <label>
        <span>${label('voice_provider_label', 'Voice')}</span>
        <select data-crypto-voice aria-label="${label('voice_provider_label', 'Voice')}">
          <option value="elevenlabs" selected>${label('voice_elevenlabs', 'ElevenLabs')}</option>
          <option value="xai">${label('voice_xai', 'xAI')}</option>
        </select>
      </label>
    </div>
    </div>`;
};

export const renderCryptoCardNote = (lang, href) => {
  const lead = escapeHtml(cryptoText(lang, 'card_checkout', 'Card checkout is on'));
  const why = escapeHtml(cryptoText(
    lang,
    'card_checkout_why',
    'Card companies will not process unrestricted generation, so those plans do not unlock it.',
  ));
  return `<p class="pricing-messages-disclaimer">${lead} <a href="${escapeHtml(href)}">kissandra.ai</a>. ${why}</p>`;
};

export const renderCryptoPricing = (lang = getCurrentLanguage(), estimate = DEFAULT_ESTIMATE) => {
  const spend = estimate?.contextTokens ? estimate : DEFAULT_ESTIMATE;
  const byok = escapeHtml(cryptoText(
    lang,
    'footnote_byok',
    'Text and voice still work with your own OpenRouter key when your balance is zero. See',
  ));
  const guide = escapeHtml(cryptoText(lang, 'footnote_byok_link', 'our guide'));
  return `
  <p class="crypto-lead">${escapeHtml(cryptoText(lang, 'subtitle', 'Pay once. Credits stay until you spend them.'))}</p>
  ${renderEstimateControls(lang, spend)}
  <div class="crypto-plan-grid">
    ${PACKS.map((pack) => renderPackCard(pack, lang, spend)).join('')}
  </div>
  <p class="pricing-messages-disclaimer">${escapeHtml(cryptoText(lang, 'footnote_mix', 'Each line under the credit count is the whole pack spent on that one thing. Mix chat, images, and video however you want.'))}</p>
  <p class="pricing-messages-disclaimer">${byok} <a href="/guide/byok#voices">${guide}</a>.</p>`;
};

const spendLine = (lang, key, fallback, count) => interpolate(cryptoText(lang, key, fallback), {
  count: formatPlanNumber(count, lang),
});

const selectedMultiplier = (select) => {
  const option = select?.selectedOptions?.[0];
  const multiplier = Number(option?.dataset?.multiplier ?? option?.value);
  return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : DEFAULT_TEXT_MULTIPLIER;
};

const applySpendLines = (root, lang) => {
  const controls = root.querySelector('.crypto-estimate-controls');
  const textSelect = root.querySelector('[data-crypto-text-model]');
  const voiceSelect = root.querySelector('[data-crypto-voice]');
  const medianChatCost = positiveNumber(controls?.dataset.medianChatCost);
  const medianVoice = positiveNumber(controls?.dataset.medianXaiVoice);
  const multiplier = selectedMultiplier(textSelect);
  const provider = voiceSelect?.value || DEFAULT_VOICE_PROVIDER;
  const perMessage = creditsPerMessage(medianChatCost, multiplier);
  const pegVoice = voiceCreditsPerMessage(medianVoice, provider);

  root.querySelectorAll('[data-pack-credits]').forEach((card) => {
    const creditValue = Number(card.getAttribute('data-pack-credits'));
    const packUsd = Number(card.getAttribute('data-pack-usd'));
    const messages = card.querySelector('[data-crypto-spend="messages"]');
    const voice = card.querySelector('[data-crypto-spend="voice"]');
    if (messages) {
      messages.textContent = spendLine(lang, 'messages', '~{count} messages', messageCount(creditValue, perMessage));
    }
    const perVoice = voiceCreditsForPack(pegVoice, creditValue, packUsd);
    if (voice && perVoice > 0) {
      voice.textContent = spendLine(lang, 'voice', '~{count} voice messages', wholePackCount(creditValue, perVoice));
    }
  });
};

const styleOptions = (styles) => {
  const seen = new Set();
  return (Array.isArray(styles) ? styles : []).flatMap((style) => {
    const name = typeof style?.name === 'string' ? style.name.trim() : '';
    if (!name || style.replaced_by || seen.has(name)) return [];
    seen.add(name);
    return [{
      name,
      multiplier: parseCreditMultiplier(style.price),
    }];
  });
};

const fillTextModelSelect = (select, styles) => {
  const options = styleOptions(styles);
  if (!select || options.length === 0) return;
  const defaultOption = options.find((option) => option.multiplier === DEFAULT_TEXT_MULTIPLIER) || options[0];
  select.replaceChildren(...options.map((option) => {
    const element = document.createElement('option');
    element.value = option.name;
    element.dataset.multiplier = String(option.multiplier);
    element.textContent = option.name;
    element.selected = option === defaultOption;
    return element;
  }));
};

const loadTextModels = async (select) => {
  try {
    const styles = await fetchCache.fetchJSON(`${CDN_URL}/data/response_styles.json`);
    fillTextModelSelect(select, styles);
  } catch (error) {
    console.error('Failed to load text models for credit estimates:', error);
  }
};

export const bindCryptoPricing = (root, onCheckout) => {
  if (!root) return;
  root.querySelectorAll('.crypto-plan-button').forEach((button) => {
    button.addEventListener('click', () => onCheckout(button));
  });
  const lang = getCurrentLanguage();
  const textSelect = root.querySelector('[data-crypto-text-model]');
  const voiceSelect = root.querySelector('[data-crypto-voice]');
  const refresh = () => applySpendLines(root, lang);
  textSelect?.addEventListener('change', refresh);
  voiceSelect?.addEventListener('change', refresh);
  loadTextModels(textSelect).then(refresh);
};
